import React, { useState, useEffect } from 'react';
import {
  Breadcrumb,
  Avatar,
  Typography,
  Space,
  Steps,
  Form,
  Input,
  Select,
  Drawer,
  Tag,
  Spin,
  Modal,
} from 'antd';
import {
  RightOutlined,
  FileTextOutlined,
  FileImageOutlined,
  FilePptOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import useAppMessage from '../hooks/useAppMessage';
import ProposalPreview from '../components/proposal/ProposalPreview';
import ProposalResult from '../components/proposal/ProposalResult';
import FilePreviewModal from '../components/common/FilePreviewModal';
import UserAvatar from '../components/common/UserAvatar';

import { useNavigate, useParams } from 'react-router-dom';
import useProposalDetailStyles from '../styles/components/proposal/ProposalDetailPage';
import drawerStyles from '../styles/components/proposal/ProposalDetailDrawer';
import FileUploadList from '../components/common/FileUploadList';
import {
  MASK_IMAGE_ENUM,
  CARD_BG_ENUM,
  PROPOSAL_TOPIC_OPTIONS,
  TRACK_OPTIONS,
  PROPOSAL_TOPIC_OPTIONS_NO_TEMPLATE,
} from '../utils/constants';
import competitionService from '../services/competitionService';
import commentService from '../services/commentService';
import Comments from '../components/comment/Comments';
import useComments from '../hooks/useComments';
import { getCurrentUser } from '../services/authService';
import shareIcon from '../assets/share.svg';
import { handleCopy } from '../utils/common';
import SharePopover from '../components/common/SharePopover';
import { SHARE_SCENE_TYPE, SHARE_QR_CONFIG } from '../utils/constants';
const { Paragraph } = Typography;
import { useEmbeddedAuth } from '../contexts/EmbeddedAuthContext';

export const ProposalDetailPage = () => {
  const { styles } = useProposalDetailStyles();
  const { id } = useParams();
  const { showLoginModal } = useEmbeddedAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    type: 'images',
    index: 0,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const navigate = useNavigate();
  const { message,contextHolder } = useAppMessage();
  
  // 新增：点赞和评论相关状态
  const [likeLoading, setLikeLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsPagination, setCommentsPagination] = useState({
    page: 1,
    total: 0,
    per_page: 20,
  });
  const [loadingComments, setLoadingComments] = useState(false);

  // 统一用同一数据源计算评论总和（与抽屉一致）
  const { comments: hookComments } = useComments(id, 1, 20);

  // 表单相关
  const [form] = Form.useForm();
  // 封面图信息（包含id、url、type等完整信息）
  const [coverFile, setCoverFile] = useState(null);
  // 立即显示的文件列表（用于 ProposalPreview）
  const [displayFiles, setDisplayFiles] = useState([]);
  // 新增：本地保存表单所有值
  const [formValues, setFormValues] = useState({
    prompt: '',
    response: '',
    topics: [],
    cover_image: '',
  });

  // 不再本地维护coverMask和coverBg，直接用后端mask
  const [optimizeMode, setOptimizeMode] = useState(false);

  // 步骤配置
  const steps = [
    {
      title: '编辑提案',
      icon: (
        <button
          className={`w-6 h-6 rounded-full text-sm text-white flex items-center justify-center ${
            currentStep >= 0 && optimizeMode
              ? 'bg-[var(--color-assist-7)]'
              : currentStep >= 0
              ? 'bg-[var(--color-primary)]'
              : 'bg-[var(--color-assist-2)]'
          }`}
        >
          1
        </button>
      ),
    },
    {
      title: '预览提交',
      icon: (
        <button
          className={`w-6 h-6 rounded-full text-sm text-white flex items-center justify-center ${
            currentStep >= 1 && optimizeMode
              ? 'bg-[var(--color-assist-7)]'
              : currentStep >= 1
              ? 'bg-[var(--color-primary)]'
              : 'bg-[var(--color-assist-2)]'
          }`}
        >
          2
        </button>
      ),
    },
  ];

  // 直接用接口返回的 attachments 作为预览数据
  // 默认选中第一个附件
  useEffect(() => {
    if (detail && detail.fields?.attachments && detail.fields.attachments.length > 0) {
      const firstAttachment = detail.fields.attachments[0];
      // 根据content_type准确判断文件类型
      let fileType = 'document';
      if (firstAttachment.content_type === 'image') {
        fileType = 'image';
      } else if (firstAttachment.content_type === 'markdown') {
        fileType = 'markdown';
      } else if (firstAttachment.content_type === 'video') {
        fileType = 'video';
      } else if (firstAttachment.content_type === 'pdf') {
        fileType = 'pdf';
      }
      
      setSelectedItem({
        type: fileType,
        index: 0,
      });
    }
  }, [detail]);

  // 新增：初始化formValues当detail加载后
  useEffect(() => {
    if (detail && detail.fields) {
      const fields = detail.fields;
      // Ensure the first topic is always included
      const firstTopic = PROPOSAL_TOPIC_OPTIONS[0]?.value;
      let topics = fields.tags || [];
      if (firstTopic && !topics.includes(firstTopic)) {
        topics = [firstTopic, ...topics];
      }

      const initialFormData = {
        prompt: fields.title || '',
        response: fields.body_text || '',
        // 过滤掉 ai_competition 和 ai_competition_model，只保留第一个有效主题
        topics: (() => {
          const filteredTopics = topics.filter(
            (t) => t !== 'ai_competition' && t !== 'ai_competition_model'
          );
          return filteredTopics.length > 0 ? [filteredTopics[0]] : [];
        })(),
        cover_image: fields.cover_image || '',
      };

      setFormValues(initialFormData);
      // 初始化封面图信息
      if (fields.cover_image) {
        setCoverFile({
          id: fields.cover_image,
          url: fields.cover_image, // 使用cover_image作为URL，因为它可能是完整的URL或者ID
          type: 'image/png', // 默认类型，实际应该从后端获取
          name: '封面图',
          uid: 'thumb-cover',
          status: 'done',
        });
      }

      // 重新设置表单字段值
      form.setFieldsValue({
        prompt: fields.title || '',
        response: fields.body_text || '',
        // 过滤掉 ai_competition 和 ai_competition_model，只保留第一个有效主题
        topics: (() => {
          const filteredTopics = topics.filter(
            (t) => t !== 'ai_competition' && t !== 'ai_competition_model'
          );
          return filteredTopics.length > 0 ? [filteredTopics[0]] : [];
        })(),
        cover_image: [],
      });
    }
  }, [detail, form]);

  // 获取当前选中的附件
  const getCurrentItem = () => {
    const allItems = getProposalPreviewItems();
    if (!allItems || allItems.length === 0) return null;
    const { index } = selectedItem;
    const item = allItems[index] || null;
    if (item) {
      // 根据content_type准确判断文件类型（兼容简写与MIME）
      let fileType = 'document';
      if (item.content_type === 'image' || item.content_type?.startsWith('image/')) {
        fileType = 'image';
      } else if (item.content_type === 'markdown' || item.content_type === 'text/markdown' || (item.filename && item.filename.toLowerCase().endsWith('.md'))) {
        fileType = 'markdown';
      } else if (item.content_type === 'video' || item.content_type?.startsWith('video/')) {
        fileType = 'video';
      } else if (item.content_type === 'pdf' || item.content_type === 'application/pdf' || (item.filename && item.filename.toLowerCase().endsWith('.pdf'))) {
        fileType = 'pdf';
      }
      
      // 确保返回的数据结构符合组件期望
      const result = {
        ...item,
        type: fileType,
        title: item.filename || item.file_name || item.title || '未命名文件',
        url: item.url || '',
        // 添加一些默认值
        likeCount: item.likeCount || 0,
        // 确保所有必要的字段都存在
        filename: item.filename || item.file_name,
        file_name: item.file_name || item.filename,
        content_type: item.content_type,
        size: item.size,
        status: item.status || 'done',
        // 确保content字段被正确传递
        content: item.content || item.fields?.content,
        // 确保fields结构完整
        fields: {
          ...item.fields,
          content: item.content || item.fields?.content
        }
      };


      return result;
    }
    return null;
  };

  const getIcon = (type) => {
    // 处理content_type格式
    if (type?.includes('/')) {
      switch (type) {
        case 'image/jpeg':
        case 'image/png':
        case 'image/jpg':
        case 'image/gif':
        case 'image/webp':
          return <FileImageOutlined />;
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'application/vnd.ms-powerpoint':
          return <FilePptOutlined />;
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/msword':
          return <FileTextOutlined />;
        case 'text/markdown':
          return <FileTextOutlined style={{ color: '#1890ff' }} />; // 蓝色图标表示Markdown
        case 'video/mp4':
        case 'video/avi':
        case 'video/mov':
        case 'video/webm':
        case 'video/ogg':
          return <FileTextOutlined style={{ color: '#722ed1' }} />; // 紫色图标表示视频
        default:
          return <FileTextOutlined />;
      }
    }

    // 处理简化的type格式
    switch (type) {
      case 'image':
        return <FileImageOutlined />;
      case 'ppt':
      case 'presentation':
        return <FilePptOutlined />;
      case 'word':
      case 'document':
        return <FileTextOutlined />;
      case 'markdown':
        return <FileTextOutlined style={{ color: '#1890ff' }} />; // 蓝色图标表示Markdown
      case 'video':
        return <FileTextOutlined style={{ color: '#722ed1' }} />; // 紫色图标表示视频
      default:
        return <FileTextOutlined />;
    }
  };

  // 优化类型判断函数
  const getTypeName = (type, content_type, name) => {
    // 优先content_type
    const t = content_type || type || '';
    if (t.startsWith('image/')) return '图片';
    if (t.startsWith('video/')) {
      // 根据视频格式返回更具体的名称
      if (t === 'video/mp4') return 'MP4视频';
      if (t === 'video/avi') return 'AVI视频';
      if (t === 'video/mov') return 'MOV视频';
      if (t === 'video/webm') return 'WebM视频';
      if (t === 'video/ogg') return 'OGG视频';
      return '视频';
    }
    if (t === 'application/pdf' || (name && name.endsWith('.pdf')))
      return 'PDF';
    if (t === 'text/markdown' || (name && name.endsWith('.md')))
      return 'Markdown';
    if (t.includes('presentation') || (name && /\.(ppt|pptx)$/i.test(name)))
      return 'PPT';
    if (t.includes('wordprocessing') || (name && /\.(doc|docx)$/i.test(name)))
      return '文档';
    return '文件';
  };

  // 编辑模式下可自定义实现
  const handleStartEdit = () => {
    setIsEditMode(true);
    setCurrentStep(0);
    // 清除之前可能残留的显示文件状态
    setDisplayFiles([]);
  };

  const handleBack = () => {
    setIsEditMode(false);
    setCurrentStep(0);
    setOptimizeMode(false);
    // 清除编辑时的显示文件状态
    setDisplayFiles([]);
  };

  const handleSave = () => {
    // 保存逻辑
  };

  const handleSaveAndNext = () => {
    // 保存并进入下一步
    handleSave();
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  // 新增：处理文件上传（立即更新显示状态）
  const handleFileUpload = async (uploadedFiles) => {
    // 在提交过程中禁用文件上传
    if (isSubmitting) {
      return;
    }
 
    try {
      // 立即转换文件格式并更新显示状态
      const newFiles = uploadedFiles.map((file) => ({
        id: file.id,
        uid: file.uid || file.id,
        url: file.originFileObj
          ? URL.createObjectURL(file.originFileObj)
          : file.url || file.fields?.file_url || '',
        name: file.name || file.file_name || file.filename || file.fields?.name,
        file_name: file.name || file.file_name || file.filename || file.fields?.name,
        filename: file.name || file.file_name || file.filename || file.fields?.name,
        title: file.name || file.file_name || file.filename || file.fields?.name,
        // 优化后的类型判断逻辑，优先使用 content_type，其次 type，最后根据文件名后缀判断
        type: file.content_type || file.type || file.fields?.mime_type,
        content_type: file.content_type || file.type || file.fields?.content_type,
        size: file.size || file.fields?.file_size,
        status: 'done',
        originFileObj: file.originFileObj,
        thumbUrl: file.thumbUrl || null, // 添加缩略图支持
        // 添加其他可能需要的字段
        metadata: file.metadata || file.fields?.metadata,
        editable: file.editable || file.fields?.editable,
        user_id: file.user_id || file.author_id,
      }));
      // 立即更新显示文件列表
      setDisplayFiles((prev) => {
        const updated = [...prev, ...newFiles];
        return updated;
      });

      // 如果是第一个文件，选中它
      setSelectedItem((prev) => {
        const currentDisplayLength = displayFiles.length;
        const currentAttachmentsLength = attachments?.length || 0;
        const totalCurrentFiles =
          currentDisplayLength + currentAttachmentsLength;

        if (totalCurrentFiles === 0 && newFiles.length > 0) {
          return {
            type: newFiles[0].type,
            index: currentAttachmentsLength, // 新文件的索引在原始附件之后
          };
        }
        return prev;
      });
    } catch (error) {
      console.error('文件上传处理失败:', error);
    }
  };

  // 新增：处理文件删除
  const handleFileDelete = (fileIndex) => {
    // 在提交过程中禁用文件删除
    if (isSubmitting) {
      return;
    }

    try {
      const allItems = getProposalPreviewItems();
      const attachmentsLength = attachments?.length || 0;

      if (fileIndex < attachmentsLength) {
        // 删除原始附件，直接从 detail.fields.attachments 中移除
        const newAttachments = attachments.filter(
          (_, idx) => idx !== fileIndex
        );
        setDetail((prev) => ({ 
          ...prev, 
          fields: {
            ...prev.fields,
            attachments: newAttachments
          }
        }));
      } else {
        // 删除新上传的文件
        const displayFileIndex = fileIndex - attachmentsLength;
        setDisplayFiles((prev) => {
          const updated = prev.filter((_, index) => index !== displayFileIndex);
          return updated;
        });
      }

      // 如果删除的是当前选中的文件，需要重新选择
      if (selectedItem.index === fileIndex) {
        const remainingItems = allItems.filter(
          (_, index) => index !== fileIndex
        );
        if (remainingItems.length > 0) {
          setSelectedItem({
            type: remainingItems[0].type,
            index: 0,
          });
        }
      }
    } catch (error) {
      console.error('文件删除处理失败:', error);
    }
  };

  // 新增：处理文件更新
  const handleFileUpdate = (updatedFile) => {
    // 在提交过程中禁用文件更新
    if (isSubmitting) {
      return;
    }

    try {
      
      // 更新原始附件列表中的文件
      setDetail((prev) => {
        const currentAttachments = prev?.fields?.attachments || [];
        const updatedAttachments = currentAttachments.map(attachment => {
          if (attachment.id === updatedFile.id) {
            // 确保更新后的文件包含最新的content字段
            return {
              ...attachment,
              ...updatedFile,
              // 特别确保fields.content字段被正确更新
              fields: {
                ...attachment.fields,
                ...updatedFile.fields,
                content: updatedFile.content || updatedFile.fields?.content || attachment.fields?.content
              }
            };
          }
          return attachment;
        });
        
        return {
          ...prev,
          fields: {
            ...prev.fields,
            attachments: updatedAttachments
          }
        };
      });

      // 更新显示文件列表中的文件
      setDisplayFiles((prev) => 
        prev.map(file => 
          file.id === updatedFile.id ? {
            ...file,
            ...updatedFile,
            // 确保content字段被正确更新
            content: updatedFile.content || updatedFile.fields?.content || file.content,
            fields: {
              ...file.fields,
              ...updatedFile.fields,
              content: updatedFile.content || updatedFile.fields?.content || file.fields?.content
            }
          } : file
        )
      );

      // 强制触发重新渲染
      setRefreshFlag(prev => prev + 1);

      // window.message?.success('文件更新成功');
    } catch (error) {
      console.error('文件更新处理失败:', error);
      window.message?.error('文件更新失败');
    }
  };

  // 渲染预览内容
  const renderPreview = (item) => {
    // 检查item是否为null或undefined
    if (!item) {
      return (
        <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-xl">
          <div className="text-center">
            <div className="text-6xl text-gray-300 mb-4">📄</div>
            <div className="text-lg font-medium text-gray-600 mb-2">
              暂无文件
            </div>
            <div className="text-sm text-gray-500">
              请选择要预览的文件
            </div>
          </div>
        </div>
      );
    }

    return (
      <FilePreviewModal
        inline={true}
        file={{
          content_type: item.content_type,
          file_name: item.filename || item.file_name,
          filename: item.filename || item.file_name,
          url: item.url,
          title: item.title || item.filename || item.file_name,
          thumbUrl: item.thumbUrl,
          // 确保传递最新的content字段
          content: item.content || item.fields?.content,
          // 确保传递完整的fields结构
          fields: item.fields
        }}
        optimizeMode={optimizeMode}
      />
    );
  };

  // Tab切换状态
  const [activeTab, setActiveTab] = useState('detail'); // 'detail' | 'result'


  // 新增：加载评论列表
  const loadComments = async (proposalId, page = 1) => {
    setLoadingComments(true);
    try {
      const perPage = commentsPagination.per_page || 20;
      const response = await commentService.getComments(proposalId, page, perPage);
      const commentList = Array.isArray(response?.comments) ? response.comments : [];

      let mergedComments = commentList;
      if (page === 1) {
        setComments(commentList);
      } else {
        setComments(prev => {
          mergedComments = [...prev, ...commentList];
          return mergedComments;
        });
      }

      const meta = response?.metadata || {};
      const resolvedPerPage = meta.per_page || perPage;
      const totalCount = meta.total || meta.total_count || meta.total_entries || mergedComments.length;

      setCommentsPagination({
        page: meta.page || page,
        total: totalCount,
        per_page: resolvedPerPage,
      });
    } catch (error) {
      console.error('加载评论失败:', error);
      window.message?.error('加载评论失败');
    } finally {
      setLoadingComments(false);
    }
  };

  // 新增：点赞功能
  const handleLike = async () => {
    if (!detail || likeLoading) return;
    setLikeLoading(true);
    try {
      await competitionService.toggleLike(detail.id);
      setDetail(prev => {
        if (!prev || !prev.fields) return prev;
        const wasLiked = prev.is_liked || false;
        const currentLikeCount = prev.fields.meta?.like_count || 0;
        const isLiked = !wasLiked;
        let newLikeCount = currentLikeCount;
        
        if (isLiked && !wasLiked) {
          newLikeCount = currentLikeCount + 1;
        } else if (!isLiked && wasLiked) {
          newLikeCount = Math.max(currentLikeCount - 1, 0);
        }
        
        return {
          ...prev,
          is_liked: isLiked,
          fields: {
            ...prev.fields,
            meta: {
              ...prev.fields.meta,
              like_count: newLikeCount,
            }
          }
        };
      });
      window.message?.success(!detail.is_liked ? '点赞成功' : '取消点赞');
    } catch (error) {
      console.error('点赞失败:', error);
      window.message?.error('操作失败');
    } finally {
      setLikeLoading(false);
    }
  };

  // 新增：加载更多评论
  const loadMoreComments = async () => {
    // 检查是否还有更多评论可以加载
    if (commentsPagination.total && comments.length >= commentsPagination.total) return;
    const nextPage = (commentsPagination.page || 1) + 1;
    await loadComments(id, nextPage);
  };

  // 新增：刷新评论
  const refreshComments = async () => {
    await loadComments(id, 1);
    // 更新评论数量
    setDetail(prev => {
      if (!prev || !prev.fields) return prev;
      return {
        ...prev,
        fields: {
          ...prev.fields,
          meta: {
            ...prev.fields.meta,
            comment_count: (prev.fields.meta?.comment_count || 0) + 1,
          }
        }
      };
    });
    window.message?.success('评论发送成功');
  };


  useEffect(() => {
    if (!id) return;
    setLoading(true);
    // 清除可能残留的显示文件状态
    setDisplayFiles([]);
    // 新增：获取提案详情接口
    competitionService
      .getEntryDetail(id)
      .then((res) => {
        setDetail(res);
        // 获取评论列表
        if (res) {
          loadComments(id, 1);
        }
      })
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [id]);

  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUserId(user?.id || null);
  }, []);

  // 确保在非编辑模式下optimizeMode始终为false
  useEffect(() => {
    if (!isEditMode) {
      setOptimizeMode(false);
    }
  }, [isEditMode]);

  // 编辑提案API
  const handleUpdateProposal = async () => {
    // 防止重复提交
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      // 直接使用formValues状态作为数据源
      
      // 使用formValues作为主要数据源
      const currentFormValues = {
        prompt: formValues.prompt || '',
        response: formValues.response || '',
        topics: formValues.topics || [],
        cover_image: formValues.cover_image || '',
      };

      // 校验必填字段
      if (!currentFormValues.prompt) throw new Error('标题不能为空');
      if (!currentFormValues.response) throw new Error('简介不能为空');
      if (!coverFile?.id) throw new Error('请上传封面图');

      // 整理附件数据
      const finalAttachments = [];

      // 添加保留的原始附件
      const currentAttachments = detail?.fields?.attachments || [];
      currentAttachments.forEach((attachment) => {
        if (attachment.id) {
          finalAttachments.push({ id: attachment.id });
        }
      });

      // 添加新上传的文件
      displayFiles.forEach((file) => {
        if (file.id) {
          finalAttachments.push({ id: file.id });
        }
      });

      if (finalAttachments.length === 0) throw new Error('请至少上传一个附件');
      
      // 构建tags数组，保留原始的ai_competition和ai_competition_model
      const originalTags = detail?.fields?.tags || [];
      const preservedTags = originalTags.filter(tag => 
        tag === 'ai_competition' || tag === 'ai_competition_model'
      );
      const finalTags = [...preservedTags, ...(currentFormValues.topics || [])];

      // 构建payload，使用新的字段结构
      const payload = {
        fields: {
          title: currentFormValues.prompt,
          body_text: currentFormValues.response,
          cover_image: coverFile.id,
          tags: finalTags,
          mask: mask,
          status: 'published',
          scope: 'square',
          category: 'competition',
        },
        content_type: 'article',
        attachments: finalAttachments,
      };

      // 提交更新
      await competitionService.updateEntry(entryId, payload);

      // 清除本地上传的文件状态
      setDisplayFiles([]);

      // 重新获取最新数据
      try {
        const updatedDetail = await competitionService.getEntryDetail(entryId);
        setDetail(updatedDetail);
      } catch (fetchError) {
      }

      setIsEditMode(false);
      message?.success?.('提案修改成功');
      
    } catch (error) {
      console.error('提案修改失败:', error);
      message?.error?.(error.message || '提案修改失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 删除提案逻辑（用受控Modal）
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const handleDeleteProposal = () => {
    setDeleteModalOpen(true);
  };
  const handleDeleteConfirm = async () => {
    try {
      await competitionService.deleteEntry(entryId);
      window.message?.success('提案已删除');
      setDeleteModalOpen(false);
      navigate('/tasks');
    } catch (error) {
      window.message?.error(error.message || '删除失败');
    }
  };
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  // 处理用户点击，跳转到个人详情页
  const handleUserClick = () => {
    const user = getCurrentUser();
    if (creator && creator.id) {
      if (!user) {
        showLoginModal();
        return;
      }
      navigate(`/personal-info/${creator.id}`);
    }
  };

  if (loading) {
    return (
      <div className={styles.centerContainer}>
        <Spin size="large" />
      </div>
    );
  }
  if (!detail) {
    return <div className={styles.centerContainer}>未找到数据</div>;
  }

  // 根据新的数据结构提取字段
  const {
    id: entryId,
    fields = {},
    author_id,
    content_type,
    inserted_at,
    updated_at,
    user = {},
  } = detail || {};
  
  // 从 fields 对象中提取具体字段
  const {
    title: prompt,
    body_text: response,
    mask,
    tags: topics,
    published: is_public,
    cover_image,
    meta = {},
    published_at,
    scope,
    group_id,
  } = fields;
  
  // 创建 creator 对象，优化用户信息提取
  const creator = {
    id: author_id || user.id,
    nickname: user.name || user.nickname || '用户',
    avatar: user.avatar || null,
  };
  
  // 附件信息（从fields.attachments中获取）
  const attachments = detail?.fields?.attachments || [];
  
  // 评论信息（从 meta 中获取）
  const reviews = [];

  // 适配mask字段，模仿ChallengeCard.js
  let maskImage = '';
  let cardBg = '';
  if (mask && mask.image && MASK_IMAGE_ENUM[mask.image]) {
    maskImage = MASK_IMAGE_ENUM[mask.image];
  }
  if (mask && mask.color && CARD_BG_ENUM[mask.color]) {
    cardBg = CARD_BG_ENUM[mask.color];
  }

  // 获取 ProposalPreview 要显示的文件列表
  const getProposalPreviewItems = () => {
    // 合并原始附件和显示文件列表
    const allItems = [...(attachments || []), ...displayFiles];

    return allItems;
  };

  // 判断是否为本人
  const isSelf =
    currentUserId && creator && creator.id && currentUserId === creator.id;

  return (
    <div className={styles.container}>
      {contextHolder}
      <div className={styles.content}>
        <div
          className={`${styles.breadcrumbWrapper} ${
            optimizeMode
              ? 'bg-[var(--color-assist-6)]'
              : 'bg-[var(--color-assist-4)]'
          }`}
        >
          {/* 面包屑导航 */}
          <div className={styles.breadcrumb}>
            <Breadcrumb
              separator={<RightOutlined />}
              items={[
                {
                  title: (
                    <span
                      className="cursor-pointer flex items-center gap-1 text-xs"
                      onClick={() => navigate(-1)}
                    >
                      <ArrowLeftOutlined className="text-xs" />
                      首页
                    </span>
                  ),
                },
              ]}
            />
          </div>

          {/* 提案标题和关注/分享按钮 */}
          <div className="flex flex-col">
            <div className={styles.titleSection}>
              <div
                className={`${styles.title} ${styles.twoLineEllipsis}`}
                title={prompt}
              >
                {prompt}
              </div>
              <div className="flex gap-6">
                <SharePopover
                  message={message}
                  userInfo={detail}
                  basicUserInfo={creator}
                  qrConfig={SHARE_QR_CONFIG[SHARE_SCENE_TYPE.PROPOSAL]}
                >
                  <button className="flex flex-col gap-1 items-center text-white hover:text-gray-300 bg-transparent border-none cursor-pointer">
                    <img src={shareIcon} alt="分享" className="w-4 h-4" />
                    分享
                  </button>
                </SharePopover>
                {/* 只有本人才能看到删除按钮 */}
                {isSelf && (
                  <button
                    className="flex flex-col gap-1 items-center text-gray-600 hover:text-red-600 bg-transparent border-none cursor-pointer"
                    onClick={handleDeleteProposal}
                  >
                    <DeleteOutlined className="w-4 h-4" />
                    删除
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 提案描述和缩略图 */}
          {!isEditMode && (
            <div className={styles.descriptionSection}>
              <div className={styles.descriptionContent}>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-300">ID: {entryId}</span>
                  <span className="text-xs text-gray-300 cursor-pointer" onClick={() => handleCopy(entryId, message)}>
                    copy
                  </span>
                </div>
                <div className={styles.threeLineEllipsis}>
                  <Paragraph title={response}>{response}</Paragraph>
                </div>
                {/* 来源信息 */}
                {!isEditMode && (
                  <div className={styles.sourceInfo}>
                    <div className={styles.userInfo}>
                      <UserAvatar 
                        size={24} 
                        src={creator.avatar}
                        name={creator.nickname}
                        className="!cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={handleUserClick}
                      />
                      <div className={`${styles.userDetails} align-middle`}>
                        <div className="user-meta">
                          <span
                            className={`user-name align-middle ${styles.userNameEllipsis} !cursor-pointer hover:text-[var(--color-primary)] transition-colors`}
                            onClick={handleUserClick}
                          >
                            {creator.nickname}
                          </span>
                        </div>
                        {topics && topics.length > 0 && (
                          <Space size={8}>
                            {topics
                              .filter(topic => 
                                topic !== 'ai_competition' && 
                                topic !== 'ai_competition_model'
                              )
                              .map((topic, index) => {
                                const topicObj = PROPOSAL_TOPIC_OPTIONS.find(
                                  (opt) => opt.value === topic
                                );

                                // 判断是否为 TRACK_OPTIONS 中的值
                                const isTrackOption =
                                  TRACK_OPTIONS.includes(topic);

                                return (
                                  <Tag
                                    key={topic + index}
                                    className={`rounded-full px-3 py-1 border text-xs ${
                                      isTrackOption
                                        ? 'bg-[#D9ADFF]'
                                        : 'bg-[var(--color-assist-2)]'
                                    }`}

                                  >
                                    {topicObj ? topicObj.label : topic}
                                  </Tag>
                                );
                              })}
                          </Space>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.thumbnailContainer}>
                {coverFile && (coverFile.url || coverFile.thumbUrl) && (
                  <img
                    src={coverFile.url || coverFile.thumbUrl}
                    alt="提案缩略图"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className={`${styles.previewSection} relative ${
            optimizeMode
              ? 'bg-[var(--color-assist-6)]'
              : 'bg-[var(--color-assist-4)]'
          }`}
        >
          {/* 编辑模式步骤 */}
          {isEditMode && (
            <div
              className={`${styles.stepsSection} ${
                optimizeMode
                  ? 'bg-[var(--color-assist-6)]'
                  : 'bg-[var(--color-assist-4)]'
              }`}
            >
              <Steps
                current={currentStep}
                items={steps.map((step, idx) => ({
                  title: step.title,
                  icon: step.icon,
                }))}
                size="small"
                labelPlacement="vertical"
                className={`p-2`}
              />
              {/* 编辑提案第一个页面内容 */}
              {currentStep === 0 && (
                <div
                  className={`${styles.editMainWrapper} ${
                    optimizeMode ? 'bg-[var(--color-assist-6)]' : ''
                  }`}
                >
                  {/* 上半部分：左右结构 */}
                  <div className={styles.editTopRow}>
                    {/* 左侧字段表单 */}
                    <div className={styles.editFormLeft}>
                      <Form
                        layout="horizontal"
                        form={form}
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        labelAlign="left"
                        initialValues={{
                          prompt: prompt,
                          response: response,
                          topics: (() => {
                            // 过滤掉 ai_competition 和 ai_competition_model
                            let filteredTopics =
                              (topics || []).filter(
                                (t) =>
                                  t !== 'ai_competition' &&
                                  t !== 'ai_competition_model'
                              ) || [];
                            // 如果有过滤后的主题，取第一个作为单选框的值
                            return filteredTopics.length > 0
                              ? [filteredTopics[0]]
                              : [];
                          })(),
                          cover_image: [],
                        }}
                        onValuesChange={(changedValues, allValues) => {
                          setFormValues((prev) => ({
                            ...prev,
                            ...allValues,
                            cover_image:
                              coverFile?.url ||
                              coverFile?.thumbUrl ||
                              prev.cover_image, // cover_image值单独处理
                          }));
                        }}
                      >
                        <Form.Item
                          label="封面图"
                          name="cover_image"
                          rules={[{ required: true, message: '请上传封面图' }]}
                          extra="仅支持图片格式"
                        >
                          <FileUploadList
                            fileList={
                              // 封面图是单独的cover_image字段，不是attachments
                              coverFile ? [coverFile] : []
                            }
                            onChange={(files) => {
                              if (files && files.length > 0) {
                                // FileUploadList上传成功后会返回包含完整信息的文件对象
                                const uploadedFile = files[0];
                                setCoverFile(uploadedFile);
                                setFormValues((prev) => ({
                                  ...prev,
                                  cover_image:
                                    uploadedFile.url ||
                                    uploadedFile.thumbUrl ||
                                    '',
                                }));
                              } else {
                                // 文件列表为空，说明删除了封面图
                                setCoverFile(null);
                                setFormValues((prev) => ({
                                  ...prev,
                                  cover_image: '',
                                }));
                              }
                            }}
                            accept="image/*"
                            maxCount={1}
                          />
                        </Form.Item>
                        <Form.Item
                          label="标题"
                          name="prompt"
                          rules={[
                            { required: true, message: '请输入标题' },
                            { max: 30, message: '标题不能超过30个字符' },
                          ]}
                        >
                          <Input
                            placeholder="请输入提案标题"
                            maxLength={30}
                            showCount
                          />
                        </Form.Item>
                        <Form.Item
                          label="简介"
                          name="response"
                          rules={[
                            { required: true, message: '请输入简介' },
                            { max: 200, message: '简介不能超过80个字符' },
                          ]}
                        >
                          <Input.TextArea
                            rows={3}
                            placeholder="请输入简介"
                            maxLength={200}
                            showCount
                          />
                        </Form.Item>
                        <Form.Item
                          label="场景"
                          name="topics"
                          rules={[
                            {
                              required: (() => {
                                // 检查原有的 topics 中是否包含 ai_competition_model
                                const originalTopics = (topics || []);
                                return originalTopics.includes(
                                  'ai_competition_model'
                                );
                              })(),
                              message: '请选择场景',
                            },
                          ]}
                        >
                          <Select
                            placeholder="请选择场景"
                            options={PROPOSAL_TOPIC_OPTIONS_NO_TEMPLATE}
                            allowClear
                            onChange={(value) => {
                              // 单选框，直接设置值
                              form.setFieldValue(
                                'topics',
                                value ? [value] : []
                              );
                              setFormValues((prev) => ({
                                ...prev,
                                topics: value ? [value] : [],
                              }));
                            }}
                          />
                        </Form.Item>
                      </Form>
                    </div>
                    {/* 右侧封面图预览 */}
                    <div
                      className={`${styles.coverPreviewRight} flex flex-col gap-2`}
                    >
                      {/* 复用ChallengeCard的缩略图mask和背景色渲染方式，全部用css-in-js */}

                      <div
                        className={`${styles.coverPreviewCard} ${styles.coverPreviewContainer}`}
                        style={{
                          '--cover-bg': cardBg,
                          '--cover-mask': `url(${maskImage})`,
                        }}
                      >
                        {coverFile && (coverFile.url || coverFile.thumbUrl) && (
                          <img
                            src={coverFile.url || coverFile.thumbUrl}
                            alt="封面预览"
                            className={styles.coverPreviewImg}
                          />
                        )}
                        {!coverFile && (
                          <div
                            className={`${styles.coverPreviewImg} ${styles.coverImagePlaceholder}`}
                          >
                            暂无封面图
                          </div>
                        )}
                        {/* 标题和简介内容，绝对定位在下半部分 */}
                        <div
                          className={`${`${styles.coverPreviewTitle} ${styles.twoLineEllipsis}`}
                         ${styles.twoLineEllipsis}`}
                          title={form.getFieldValue('prompt') || prompt}
                        >
                          {form.getFieldValue('prompt') || prompt}
                        </div>
                        <div
                          className={styles.coverPreviewSubtitle}
                          title={form.getFieldValue('response') || response}
                        >
                          {form.getFieldValue('response') || response}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 下半部分：上传文件列表 - 仅在第一步显示 */}
                  {currentStep === 0 && (
                    <div className={styles.uploadListSection}>
                      <div className={styles.uploadListTitle}>文件列表</div>
                      <ProposalPreview
                        previewContent={response}
                        allItems={getProposalPreviewItems()}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        getCurrentItem={getCurrentItem}
                        getIcon={getIcon}
                        getTypeName={getTypeName}
                        renderPreview={renderPreview}
                        styles={styles}
                      setCommentModalVisible={setCommentModalVisible}
                      optimizeMode={optimizeMode}
                      scrollContainerRef={null}
                      showLeftScroll={false}
                      showRightScroll={false}
                      scrollLeft={() => {}}
                      scrollRight={() => {}}
                      handleMouseDown={() => {}}
                      handleMouseMove={() => {}}
                      handleMouseUp={() => {}}
                      handleMouseLeave={() => {}}
                      isDragging={false}
                      onFileUpload={handleFileUpload}
                      onFileUpdate={handleFileUpdate}
                      onFileDelete={handleFileDelete}
                      isEditMode={isEditMode}
                      currentStep={currentStep}
                      isSubmitting={isSubmitting}
                      commentsPagination={commentsPagination}
                      />
                    </div>
                  )}
                </div>
              )}
              {currentStep === 1 && (
                <>
                  <div
                    className={`styles.breadcrumbWrapper ${
                      isEditMode ? 'bg-[#fff] p-2 rounded-xl' : ''
                    }`}
                  >
                    {/* 面包屑导航 */}
                    <div className={styles.breadcrumb}>
                      <Breadcrumb
                        separator={<RightOutlined />}
                        items={[
                          {
                            title: (
                              <span className={styles.breadcrumbItem}>
                                <ArrowLeftOutlined
                                  className={styles.breadcrumbArrow}
                                />
                                首页
                              </span>
                            ),
                          },
                        ]}
                      />
                    </div>

                    {/* 提案标题和关注/分享按钮 */}
                    <div className={styles.titleSection}>
                      <div
                        className={`${styles.title} ${styles.twoLineEllipsis}`}
                        title={form.getFieldValue('prompt') || prompt}
                      >
                        {form.getFieldValue('prompt') || prompt}
                      </div>

                      <div className="flex gap-2">
                        <SharePopover
                          message={message}
                          userInfo={detail}
                          basicUserInfo={creator}
                          qrConfig={SHARE_QR_CONFIG[SHARE_SCENE_TYPE.PROPOSAL]}
                        >
                          <button className="flex flex-col gap-1 items-center text-white hover:text-gray-300 bg-transparent border-none cursor-text">
                            <img src={shareIcon} alt="分享" className="w-4 h-4" />
                            分享
                          </button>
                        </SharePopover>
                      </div>
                    </div>

                    {/* 提案描述和缩略图 */}
                    <div className={styles.descriptionSection}>
                      <div className={styles.descriptionContent}>
                        <div className={styles.threeLineEllipsis}>
                          <Paragraph
                            title={form.getFieldValue('response') || response}
                          >
                            {form.getFieldValue('response') || response}
                          </Paragraph>
                        </div>
                        {/* 来源信息 */}
                        <div className={styles.sourceInfo}>
                          <div className={styles.userInfo}>
                            <UserAvatar size={24} src={creator.avatar} name={creator.nickname} />
                            <div
                              className={`${styles.userDetails} align-middle`}
                            >
                              <div className="user-meta">
                                by
                                <span
                                  className={`user-name align-middle ${styles.userNameEllipsis}`}
                                >
                                  {creator.nickname}
                                </span>
                              </div>
                              {(() => {
                                // 获取表单中的 topics
                                const formTopics =
                                  form.getFieldValue('topics') || [];

                                // 获取原有的 topics，保留 ai_competition 和 ai_competition_model
                                const originalTopics = (topics || []);
                                const preservedTopics = originalTopics.filter(
                                  (topic) =>
                                    topic === 'ai_competition' ||
                                    topic === 'ai_competition_model'
                                );

                                // 合并表单中的 topics 和保留的原有 topics
                                const displayTopics = [
                                  ...preservedTopics,
                                  ...formTopics,
                                ];

                                return (
                                  displayTopics &&
                                  displayTopics.length > 0 && (
                                    <Space size={8}>
                                      {displayTopics
                                        .filter(topic => 
                                          topic !== 'ai_competition' && 
                                          topic !== 'ai_competition_model'
                                        )
                                        .map((topic, index) => {
                                          const topicObj =
                                            PROPOSAL_TOPIC_OPTIONS.find(
                                              (opt) => opt.value === topic
                                            );

                                          // 判断是否为 TRACK_OPTIONS 中的值
                                          const isTrackOption =
                                            TRACK_OPTIONS.includes(topic);

                                          return (
                                            <button
                                              key={topic + index}
                                              className={`px-2 py-1 rounded-full text-xs ${
                                                isTrackOption
                                                  ? 'bg-[#D9ADFF]'
                                                  : 'bg-[var(--color-assist-2)]'
                                              }`}
                       
                                            >
                                              {topicObj ? topicObj.label : topic}
                                            </button>
                                          );
                                        })}
                                    </Space>
                                  )
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={styles.thumbnailContainer}>
                        {coverFile && (coverFile.url || coverFile.thumbUrl) && (
                          <img
                            src={coverFile.url || coverFile.thumbUrl}
                            alt="提案缩略图"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <ProposalPreview
                    previewContent={form.getFieldValue('response') || response}
                    allItems={getProposalPreviewItems()}
                    selectedItem={selectedItem}
                    setSelectedItem={setSelectedItem}
                    getCurrentItem={getCurrentItem}
                    getIcon={getIcon}
                    getTypeName={getTypeName}
                    renderPreview={renderPreview}
                    styles={styles}
                    setCommentModalVisible={setCommentModalVisible}
                    optimizeMode={optimizeMode}
                    scrollContainerRef={null}
                    showLeftScroll={false}
                    showRightScroll={false}
                    scrollLeft={() => {}}
                    scrollRight={() => {}}
                    handleMouseDown={() => {}}
                    handleMouseMove={() => {}}
                    handleMouseUp={() => {}}
                    handleMouseLeave={() => {}}
                    isDragging={false}
                    onFileUpload={handleFileUpload}
                    onFileUpdate={handleFileUpdate}
                    onFileDelete={handleFileDelete}
                    isEditMode={isEditMode}
                    isSubmitting={isSubmitting}
                    commentsPagination={commentsPagination}
                  />
                </>
              )}
            </div>
          )}
          {/* 预览区域 */}
          {!isEditMode && (
            <div className={styles.tabNavigation}>
              <div
                onClick={() => setActiveTab('detail')}
                className={`${styles.tabItem} ${
                  activeTab === 'detail' ? 'active' : ''
                }`}
              >
                提案详情
              </div>
              {/* <div
                onClick={() => setActiveTab('result')}
                className={`${styles.tabItem} ${activeTab === 'result' ? 'active' : ''}`}
              >
                查看结果
              </div> */}
            </div>
          )}
          {!isEditMode &&
            (activeTab === 'detail' ? (
              <ProposalPreview
                previewContent={response}
                allItems={getProposalPreviewItems()}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                getCurrentItem={getCurrentItem}
                getIcon={getIcon}
                getTypeName={getTypeName}
                renderPreview={renderPreview}
                styles={styles}
                setCommentModalVisible={setCommentModalVisible}
                optimizeMode={optimizeMode}
                scrollContainerRef={null}
                showLeftScroll={false}
                showRightScroll={false}
                scrollLeft={() => {}}
                scrollRight={() => {}}
                handleMouseDown={() => {}}
                handleMouseMove={() => {}}
                handleMouseUp={() => {}}
                handleMouseLeave={() => {}}
                isDragging={false}
                onFileUpload={handleFileUpload}
                onFileUpdate={handleFileUpdate}
                onFileDelete={handleFileDelete}
                isEditMode={false}
                proposalData={detail}
                commentsPagination={commentsPagination}
                onLike={handleLike}
                likeLoading={likeLoading}
                isSubmitting={isSubmitting}
              />
            ) : (
              <ProposalResult
                stats={reviews}
                comments={comments}
                commentsPagination={commentsPagination}
                radarData={reviews}
                lineData={reviews}
                optimizeMode={optimizeMode}
                contentId={entryId}
              />
            ))}
          {/* 移除常驻评论区，改为仅在点击评论图标时通过抽屉展示 */}
          {/* 评论抽屉 */}
          <Drawer
            title="社区反馈"
            placement="bottom"
            onClose={() => {
              setCommentModalVisible(false);
            }}
            open={!isEditMode && activeTab === 'detail' && commentModalVisible}
            height="70%"
            width="100%"
            getContainer={false}
            classNames={{
              body: styles.drawerBody,
              header: styles.drawerHeader,
            }}
            styles={drawerStyles}
          >
            <Comments contentId={id} />
          </Drawer>
          {/* 底部操作按钮 */}
          <div
            className={`${styles.bottomActions} ${
              optimizeMode
                ? 'bg-[var(--color-assist-6)]'
                : 'bg-[var(--color-assist-4)]'
            }`}
          >
            {isEditMode && currentStep === 0 && (
              <div className={styles.editActions}>
                <button
                  className={`${styles.secondaryButton} rounded-full`}
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  返回
                </button>
                {/* <button
                  className={`${styles.secondaryButton} rounded-full`}
                  onClick={handleSave}
                >
                  保存
                </button> */}
                <button
                  className={`${styles.primaryButton} rounded-full !text-black ${
                    optimizeMode
                      ? 'bg-[var(--color-assist-7)] hover:bg-[#e09b0c] border-[var(--color-assist-7)] hover:border-[#e09b0c]'
                      : ''
                  }`}
                  style={
                    optimizeMode
                      ? {
                          backgroundColor: 'var(--color-assist-7)',
                          borderColor: 'var(--color-assist-7)',
                        }
                      : {}
                  }
                  onClick={handleSaveAndNext}
                  disabled={isSubmitting}
                >
                  保存并进入下一步
                </button>
                {/* <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    迭代模式
                  </span>
                  <Switch
                    checked={optimizeMode}
                    onChange={(checked) => {
                      setOptimizeMode(checked);
                      if (checked) {
                        setCurrentStep(0);
                      }
                    }}
                    checkedChildren="开"
                    unCheckedChildren="关"
                    size="small"
                    className={optimizeMode ? styles.optimizeModeSwitch : ''}
                  />
                </div> */}
              </div>
            )}
            {isEditMode && currentStep === 1 && (
              <div className={styles.editActions}>
                <button
                  className={`${styles.secondaryButton} rounded-full`}
                  onClick={() => setCurrentStep(currentStep - 1)}
                  disabled={isSubmitting}
                >
                  上一步
                </button>
                <button
                  className={`${styles.primaryButton} rounded-full ${
                    optimizeMode
                      ? 'bg-[var(--color-assist-7)] hover:bg-[#e09b0c] border-[var(--color-assist-7)] hover:border-[#e09b0c]'
                      : ''
                  } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                  style={
                    optimizeMode
                      ? {
                          backgroundColor: 'var(--color-assist-7)',
                          borderColor: 'var(--color-assist-7)',
                        }
                      : {}
                  }
                  onClick={handleUpdateProposal}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      提交中...
                    </div>
                  ) : (
                    '确认提交此版本'
                  )}
                </button>
                {/* <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    迭代模式
                  </span>
                  <Switch
                    checked={optimizeMode}
                    onChange={(checked) => {
                      setOptimizeMode(checked);
                      if (checked) {
                        setCurrentStep(0);
                      }
                    }}
                    checkedChildren="开"
                    unCheckedChildren="关"
                    size="small"
                    className={optimizeMode ? styles.optimizeModeSwitch : ''}
                  />
                </div> */}
              </div>
            )}
            {!isEditMode && isSelf && (
              <div className={`${styles.editActions}`}>
                <button
                  className={`${styles.primaryButton} rounded-full flex items-center gap-2 !text-black`}
                  onClick={handleStartEdit}
                >
                  <EditOutlined />
                  开始编辑
                </button>
                {/* <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    迭代模式
                  </span>
                  <Switch
                    checked={false}
                    onChange={setOptimizeMode}
                    checkedChildren="开"
                    unCheckedChildren="关"
                    disabled={true}
                    size="small"
                    className={optimizeMode ? styles.optimizeModeSwitch : ''}
                  />
                </div> */}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 删除确认弹窗 */}
      <Modal
        open={deleteModalOpen}
        title="确认删除"
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="删除"
        okType="danger"
        cancelText="取消"
      >
        确定要删除该提案吗？此操作不可恢复。
      </Modal>
    </div>
  );
};

export default ProposalDetailPage;
